import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Page } from "../../layouts/Page";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { GetOpportunity } from "./queries";
import ChatContext from "../../context/ChatContext";
import classnames from "classnames";
import {
  getColumnWidth,
  onColumnResizeHof,
  getApplicantsAndInfluencers,
  onDiscontinueHof,
  onInitChatHof,
  onVisibilityChangeHof,
  onExpirationExtendedHof,
  onApplicationActionHof
} from "./helpers";
import { LEFT_COLUMNS, RIGHT_COLUMNS } from "./constants";

import {
  OpportunityDataPointCards,
  OpportunityDetailsLists,
  OpportunityDetailsHeader
} from "../../molecules/OpportunityDetails";
import { CreateApplicationEvent, UpdateOpportunity } from "./mutations";
import { Alert, Intent, TextArea } from "@blueprintjs/core";
import { useOpportunityDetailsAlert } from "./hooks";

import styles from "./OpportunityDetails.module.scss";
import { ApplicantDetailSidebar } from "../../molecules/ApplicantDetailSidebar";
import { GetApplicantDetails } from "../../molecules/ApplicantDetailSidebar/queries";
import { getProduct } from "../../molecules/ProductSidebar/queries";

function OpportunityDetails({ match }) {
  const [leftColumnWidths, setLeftColumnWidths] = useState(
    new Array(6).fill(null)
  );
  const [rightColumnWidths, setRightColumnWidths] = useState(
    new Array(6).fill(null)
  );
  const [expirationDate, setExpirationDate] = useState();
  const [alertText, setAlertText] = useState();
  const [applicantId, setApplicantId] = useState();
  const [status, setStatus] = useState();
  const [applicantType, setApplicantType] = useState("");
  const [isApplicantDetailsOpen, setIsApplicantDetailsOpen] = useState(false);

  const { otherUserId, setOtherUserId } = useContext(ChatContext);

  const openApplicantDetails = ({ id, type }) => {
    setApplicantId(id);
    setIsApplicantDetailsOpen(true);
    setApplicantType(type);
  };

  const onDismissSidebar = () => {
    setApplicantId("");
    setIsApplicantDetailsOpen(false);
    setApplicantType("");
  };

  const {
    alertState,
    setAlertState,
    dangerIcon,
    defaultIcon,
    successIcon
  } = useOpportunityDetailsAlert();

  const {
    params: { id }
  } = match;
  const [createApplicationEvent] = useMutation(CreateApplicationEvent, {
    refetchQueries: [
      {
        query: GetApplicantDetails,
        variables: { id: applicantId, opportunityId: id }
      }
    ]
  });
  const { loading, data, refetch } = useQuery(GetOpportunity, {
    variables: {
      id
    }
  });

  const opportunity =
    data && data.opportunities && data.opportunities.length
      ? data.opportunities[0]
      : {};

  const resizeLeftTablesColumns = onColumnResizeHof({
    columnWidths: leftColumnWidths,
    setColumnWidths: setLeftColumnWidths,
    columns: LEFT_COLUMNS
  });

  const resizeRightTablesColumns = onColumnResizeHof({
    columnWidths: rightColumnWidths,
    setColumnWidths: setRightColumnWidths,
    columns: RIGHT_COLUMNS
  });

  const history = useHistory();
  const readOnly = !loading && !opportunity.isActive;

  const [updateOpportunity] = useMutation(UpdateOpportunity, {
    refetchQueries: [
      {
        query: getProduct,
        variables: {
          id: opportunity && opportunity.product && opportunity.product.id
        }
      }
    ]
  });

  const onDiscontinue = onDiscontinueHof({
    dangerIcon,
    opportunity,
    history,
    setAlertState,
    updateOpportunity
  });

  const onInitChat = onInitChatHof(setOtherUserId, { history });

  const onVisibilityChange = onVisibilityChangeHof({
    opportunity,
    defaultIcon,
    setAlertState,
    updateOpportunity,
    refetch
  });

  const onExpirationExtended = onExpirationExtendedHof({
    expirationDate,
    opportunity,
    refetch,
    setAlertState,
    defaultIcon,
    updateOpportunity
  });

  const onApplicationAction = onApplicationActionHof({
    dangerIcon,
    successIcon,
    setAlertText,
    setAlertState,
    setApplicantId,
    setStatus
  });

  const { applicants, influencers } = getApplicantsAndInfluencers({
    opportunity,
    onApplicationAction,
    alertText,
    setAlertState,
    refetch,
    createApplicationEvent,
    onInitChat
  });
  return (
    <Page title="Your Opportunities" flex className={styles.page}>
      <div className={classnames(styles.header)}>
      <ApplicantDetailSidebar
        onApplicationAction={onApplicationAction}
        isApplicantDetailsOpen={isApplicantDetailsOpen}
        applicantId={applicantId}
        opportunityId={id}
        onDismissSidebar={onDismissSidebar}
        applicantType={applicantType}
        isReadOnly={readOnly}
      ></ApplicantDetailSidebar>
      <OpportunityDetailsHeader
        opportunity={opportunity}
        onVisibilityChange={onVisibilityChange}
        onExpirationChange={(date) => setExpirationDate(date)}
        onExpirationExtended={onExpirationExtended}
        onDiscontinue={onDiscontinue}
        isReadOnly={readOnly}
      />
      </div>
      <div className={classnames(styles.opportunityBody,readOnly && styles.readOnly)}>
        <OpportunityDataPointCards
          onVisibilityChange={onVisibilityChange}
          onExpirationChange={(date) => setExpirationDate(date)}
          onExpirationExtended={onExpirationExtended}
          opportunity={opportunity}
          isReadOnly={readOnly}
        />
        <OpportunityDetailsLists
          applicants={applicants}
          influencers={influencers}
          onLeftColumnResize={resizeLeftTablesColumns}
          onRightColumnResize={resizeRightTablesColumns}
          leftColumns={LEFT_COLUMNS}
          rightColumns={RIGHT_COLUMNS}
          leftColumnWidths={leftColumnWidths}
          rightColumnWidths={rightColumnWidths}
          getColumnWidth={getColumnWidth}
          isLoading={loading}
          openApplicantDetails={openApplicantDetails}
          isReadOnly={readOnly}
        />
      </div>
      <Alert
        className={styles.confirmAlert}
        isOpen={alertState.visible}
        onCancel={() => setAlertState({ visible: false })}
        onConfirm={async () => {
          await createApplicationEvent({
            variables: {
              applicationEvent: {
                opportunity: opportunity.id,
                status,
                applicant: applicantId,
                message: alertText ? alertText : undefined
              }
            }
          });

          await refetch();
          setAlertState({
            visible: false
          });
          onDismissSidebar();
        }}
        {...alertState}
      />
    </Page>
  );
}

export default OpportunityDetails;
